import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import cacheService from '../../services/cacheService';
import indexedDBService from '../../services/indexedDBService';

const LoginForm = () => {

  const API_PREFIX = '/api';
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [isTrustedDevice, setIsTrustedDevice] = useState(false);
  const [showForgotMessage, setShowForgotMessage] = useState(false);

  useEffect(() => {
    const trusted = cacheService.isTrustedDevice();
    setIsTrustedDevice(trusted);
    console.log('🔍 Device trust status:', trusted ? 'TRUSTED' : 'NOT TRUSTED');
  }, []);

    const handleForgetDevice = async () => {
      if (!window.confirm('Are you sure you want to forget this device? All cached data will be cleared.')) {
        return;
      }

      try {
        // Clear trust flag
        cacheService.setDeviceTrusted(false);
        // Clear all cached data
        await indexedDBService.clearAll();
        
        setIsTrustedDevice(false);
        setTrustDevice(false);
        setShowForgotMessage(true);
        setTimeout(() => setShowForgotMessage(false), 3000);
        console.log('✅ Device forgotten successfully');
      } catch (error) {
        console.error('Error forgetting device:', error);
        setError('Failed to forget device');
      }
    };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    localStorage.removeItem('user');
    localStorage.removeItem('pendingUser');
    console.log('Starting login attempt');
    
    const isPending = searchParams.get('pending') === 'true';
    console.log('Is pending signup?', isPending);
      
    try {
      const testResponse = await fetch(API_PREFIX + '/auth/test-cookie', {
        credentials: 'include'
      });
      console.log('Test connection response:', testResponse.status);
      
      console.log('Sending login request');
      console.log("ENV VAR:", process.env.REACT_APP_API_PREFIX);
      console.log("API_PREFIX:", API_PREFIX);
      console.log("Requesting URL:", API_PREFIX + '/auth/login');
      
      const response = await fetch(API_PREFIX + '/auth/login', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            ...formData,
            trust_device: trustDevice || isTrustedDevice
          })
        });
  
      console.log('Got response:', response.status);
      const data = await response.json();
      console.log('Response data:', data);
  
      if (!response.ok) {
        if (response.status === 403) {
          setError("Email not verified. Check your email or resend verification.");
          return;
        }
        throw new Error(data.detail || 'Login failed');
      }
  
      console.log('Login successful, storing user data');
      localStorage.setItem('user', JSON.stringify(data.user));
      
      if ((trustDevice && !isTrustedDevice) || isTrustedDevice) {
          cacheService.setDeviceTrusted(true);
          console.log('✅ Device marked as trusted');
        }
      
      console.log('DEBUG - isPending:', isPending);
      console.log('DEBUG - plan_status:', data.user.plan_status);
      console.log('DEBUG - condition result:', isPending && data.user.plan_status === 'pending_pro');

      console.log('User plan status:', data.user.plan_status);
      console.log('Is pending signup:', isPending);

      // if ((trustDevice || isTrustedDevice) && data.user.user_id) {
      //     console.log('🔄 Starting background cache sync...');
      //     cacheService.fullSync(data.user.user_id).catch(error => {
      //       console.error('Cache sync error:', error);
      //     });
      //   }
      
      if (isPending && data.user.plan_status === 'pending_pro') {
      console.log('Setting up pro user with free features first');
      try {
        const setupResponse = await fetch(API_PREFIX + '/auth/setup/initialize-pro', {
          method: 'POST',
          credentials: 'include'
        });
        
        if (setupResponse.ok) {
          console.log('Pro user setup completed, redirecting to payment');
          if (window.location.pathname.includes('/dev')) {
            window.location.href = '/dev/upgrade?welcome=true';
          } else {
            window.location.href = '/upgrade?welcome=true';
          }
        } else {
          setError('Account setup failed. Please try again.');
        }
      } catch (setupError) {
        setError('Account setup failed. Please try again.');
      }
    } else if (isPending && data.user.plan_status === 'pending_free') {
        console.log('Setting up free user account invisibly');
        try {
          const setupResponse = await fetch(API_PREFIX + '/auth/setup/initialize-free', {
            method: 'POST',
            credentials: 'include'
          });
          
          if (setupResponse.ok) {
            console.log('Free user setup completed successfully');
            if (window.location.pathname.includes('/dev')) {
              window.location.href = '/dev/dashboard';
            } else {
              window.location.href = '/dashboard';
            }
          } else {
            console.error('Setup failed:', setupResponse.status);
            setError('Account setup failed. Please try again.');
          }
        } catch (setupError) {
          console.error('Setup error:', setupError);
          setError('Account setup failed. Please try again.');
        }
      } else if (data.user.plan_status === 'active') {
          console.log('Active user - redirecting to dashboard');
          if (window.location.pathname.includes('/dev')) {
            window.location.href = '/dev/dashboard';
          } else {
            window.location.href = '/dashboard';
          }
        } else if (data.user.plan_status === 'active_pro') {
          console.log('Active pro user - redirecting to dashboard');
          if (window.location.pathname.includes('/dev')) {
            window.location.href = '/dev/dashboard';
          } else {
            window.location.href = '/dashboard';
          }
        } else if (data.user.plan_status === 'active_free') {
          console.log('Active free user - redirecting to dashboard');
          if (window.location.pathname.includes('/dev')) {
            window.location.href = '/dev/dashboard';
          } else {
            window.location.href = '/dashboard';
          }
        } else {
        console.log('Unknown plan status, redirecting to dashboard');
        if (window.location.pathname.includes('/dev')) {
          window.location.href = '/dev/dashboard';
        } else {
          window.location.href = '/dashboard';
        }
      }
      
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {searchParams.get('pending') === 'true' && (
        <div 
          className="p-3 rounded-lg border-l-4"
          style={{ 
            backgroundColor: theme.sidebarBg,
            borderLeftColor: theme.buttonBg,
            color: theme.mainPanelText 
          }}
        >
          <p className="text-sm">
            Email verified! Please log in to continue setting up your account.
          </p>
        </div>
      )}

      {showForgotMessage && (
        <div 
          className="p-3 rounded-lg border"
          style={{ 
            backgroundColor: '#f0fdf4',
            borderColor: '#bbf7d0',
            color: '#15803d'
          }}
        >
          <p className="text-sm">✅ Device forgotten. All cached data cleared.</p>
        </div>
      )}

      <div>
        <label 
          htmlFor="email" 
          className="block text-sm font-medium mb-2"
          style={{ color: theme.mainPanelText }}
        >
          Email address
        </label>
        <input
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
          style={{ 
            backgroundColor: theme.inputBg,
            borderColor: theme.inputBorder,
            color: theme.inputText
          }}
          required
        />
      </div>

      <div>
        <label 
          htmlFor="password" 
          className="block text-sm font-medium mb-2"
          style={{ color: theme.mainPanelText }}
        >
          Password
        </label>
        <input
          id="password"
          name="password"
          type="password"
          value={formData.password}
          onChange={handleChange}
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
          style={{ 
            backgroundColor: theme.inputBg,
            borderColor: theme.inputBorder,
            color: theme.inputText
          }}
          required
        />
      </div>

      {isTrustedDevice ? (
        <div 
          className="p-4 rounded-lg border"
          style={{
            backgroundColor: '#f0fdf4',
            borderColor: '#bbf7d0'
          }}
        >
          <div className="flex items-start gap-2 mb-2">
            <span style={{ color: '#15803d', fontSize: '14px', fontWeight: '600' }}>
              ✅ Trusted Device
            </span>
          </div>
          <p style={{ color: '#166534', fontSize: '13px', margin: '0 0 12px 0' }}>
            Your data is cached for fast access
          </p>
          <button
            type="button"
            onClick={handleForgetDevice}
            className="px-3 py-1.5 rounded-md border text-sm font-medium"
            style={{
              backgroundColor: 'white',
              borderColor: '#d1d5db',
              color: '#6b7280'
            }}
          >
            Forget This Device
          </button>
        </div>
      ) : (
        <div className="flex items-start">
          <input
            id="trust-device"
            name="trust-device"
            type="checkbox"
            checked={trustDevice}
            onChange={(e) => setTrustDevice(e.target.checked)}
            className="h-4 w-4 rounded focus:ring-2 cursor-pointer mt-0.5"
            style={{
              accentColor: theme.accentColor
            }}
          />
          <label 
            htmlFor="trust-device" 
            className="ml-2 block text-sm cursor-pointer"
            style={{ color: theme.mainPanelText }}
          >
            <span className="font-medium">Trust this device</span>
            <span className="block text-xs opacity-60 mt-1">
              Keep me logged in for 30 days and cache data for offline access
            </span>
          </label>
        </div>
      )}

      {error && (
        <div className="text-red-500 text-sm">{error}</div>
      )}

      <button
        type="submit"
        className="w-full py-2 px-4 rounded-md font-medium focus:outline-none focus:ring-2 transition duration-150"
        style={{
          backgroundColor: theme.buttonBg,
          color: theme.buttonText,
        }}
      >
        Sign In
      </button>

      <div className="mt-4 text-center">
        <span style={{ color: theme.mainPanelText }}>
          Don't have an account?{' '}
        </span>
        <Link 
          to="/signup" 
          className="font-medium"
          style={{ color: theme.accentColor }}
        >
          Sign Up
        </Link>
        <div className="mt-4 text-center">
          <Link 
            to="/forgot-password" 
            className="font-medium"
            style={{ color: theme.accentColor }}
          >
            Forgot Password?
          </Link>
        </div>
      </div>
    </form>
  );
};

export default LoginForm;